/**
 * Quote/Check response normalization for Sololand (only-stay)
 *
 * The Alpitour quote API returns compositions.passenger[] with per-passenger
 * field definitions (name, mandatory, value, etc.).  The Sololand /checkout
 * API returns a token (for the book API) and a passengers array where each
 * entry is a list of field names that passenger requires.
 *
 * This module converts both response shapes into the compositions.passenger
 * format that build_passengers_model() and the passenger form expect.
 */

import { markup } from '@alpitour/utilities'
import { DEFAULT_ADULT_AGE } from '@resources/passenger'

// Fields every passenger needs (fallback when API doesn't specify fields)
const PASSENGER_FIELDS = [
  'tipPax', 'sesso', 'nome', 'cognome', 'dataNascita',
  'nazionalita', 'countCell', 'numCell', 'prefCell',
]

const fields_to_details = (field_names) => ({
  fields: field_names.map(name => ({ name, value: '', mandatory: true })),
})

/**
 * Build a single passenger entry (fallback — uses hardcoded PASSENGER_FIELDS).
 */
const build_passenger = (id, type, age) => ({
  id,
  passengerType: type,
  age,
  details: fields_to_details(PASSENGER_FIELDS),
})

/**
 * Derive passenger type from age.
 */
const age_to_pax_type = (age) => age <= 1 ? 'INFANT' : (age < 18 ? 'CHILD' : 'ADULT')

/**
 * Flatten occupancy rooms into [{type, age}] entries.
 */
const flatten_occupancy = (occupancy) => {
  const rooms = Array.isArray(occupancy) ? occupancy : []
  return rooms.reduce((acc, room) => {
    const adults = Array.from({ length: room.adults || 0 }, () => ({ type: 'ADULT', age: DEFAULT_ADULT_AGE }))
    const children = (Array.isArray(room.children) ? room.children : []).map(a => {
      const age = typeof a === 'number' ? a : parseInt(a) || 5
      return { type: age_to_pax_type(age), age }
    })
    return [...acc, ...adults, ...children]
  }, [])
}

/**
 * Build compositions.passenger from the checkout API passengers field.
 *
 * The checkout API returns passengers as array of arrays — each sub-array
 * lists field names that passenger requires (already canonical English names).
 * We prepend type_pax (hidden, auto-derived) so the form title renders correctly.
 *
 * @param {Array<Array<string>>} passengers_fields
 * @param {Array} occupancy - Vuex occupancy for deriving passengerType/age
 * @returns {{ passenger: Object[] }}
 */
const build_compositions_from_passengers_fields = (passengers_fields, occupancy) => {
  const pax_types = flatten_occupancy(occupancy)

  return {
    passenger: passengers_fields.map((field_names, i) => {
      const info = pax_types[i] || { type: 'ADULT', age: DEFAULT_ADULT_AGE }
      return {
        id: i + 1,
        passengerType: info.type,
        age: info.age,
        details: fields_to_details(['type_pax', ...field_names]),
      }
    }),
  }
}

/**
 * Build compositions.passenger from occupancy data.
 *
 * occupancy shape (from Vuex global state):
 *   [{ adults: 2, children: [4, 8] }]
 *
 * @param {Array} occupancy - Array of room occupancy objects
 * @returns {{ passenger: Object[] }}
 */
export const build_compositions_from_occupancy = (occupancy) => {
  const passengers = []
  let id = 1

  const rooms = Array.isArray(occupancy) ? occupancy : []

  rooms.forEach(room => {
    const adults = room.adults || 0
    const children = Array.isArray(room.children) ? room.children : []

    for (let i = 0; i < adults; i++) {
      passengers.push(build_passenger(id++, 'ADULT', 30))
    }

    children.forEach(childAge => {
      const age = typeof childAge === 'number' ? childAge : parseInt(childAge) || 5
      const type = age <= 1 ? 'INFANT' : 'CHILD'
      passengers.push(build_passenger(id++, type, age))
    })
  })

  // Fallback: at least 1 adult
  if (passengers.length === 0) {
    passengers.push(build_passenger(1, 'ADULT', 30))
  }

  return { passenger: passengers }
}

/**
 * Build a services.hotels entry from the root-level Sololand hotel data.
 *
 * The Sololand check API returns hotel info (name, room, rate, etc.) at the
 * response root, but services.hotels is always [].  This function maps the
 * root-level data into the Alpitour hotel shape that hotels.vue expects.
 */
const build_hotel_from_root = (data, { checkin, checkout } = {}) => {
  const room = data.room || {}
  const rate = room.rate || {}
  const pricing = rate.pricing || {}

  return {
    name: data.name || '',
    checkIn: checkin || '',
    checkOut: checkout || '',
    offers: [{
      rooms: [{
        id: room.name || '',
        name: room.name || '',
        priceDescriptor: {
          netPrice: String(pricing.total || 0),
        },
        boards: [{
          id: rate.description || '',
          description: rate.description || '',
          totalPrice: '0',
          selected: true,
        }],
      }],
    }],
  }
}

/**
 * Normalize a Sololand check/checkout response into the Alpitour quote shape.
 *
 * Handles two response formats:
 * - Old check: hotel data at root, no passengers field → uses bookToken
 * - New checkout: hotel data under `property`, passengers field arrays → uses token (sololand_book_token)
 *
 * @param {Object} checkResponse - response.data.data from sololand check/checkout
 * @param {Array}  occupancy     - store.state.occupancy
 * @param {Object} dates         - { checkin, checkout } from store
 * @returns {Object} quote-shaped object
 */
export const normalize_check_to_quote = (checkResponse, occupancy, dates = {}) => {
  const data = checkResponse || {}

  // New checkout format wraps hotel data under "property"
  const source = (data.property && data.property.name) ? data.property : data

  const { room = {} } = source
  const { rate = {} } = room
  const { pricing = {} } = rate
  const base_price = pricing.total || 0

  const raw_services = data.services || { hotels: [], flights: [] }
  const services = {
    ...raw_services,
    hotels: [...(raw_services.hotels || [])],
    travelSupplements: raw_services.travelSupplements || [],
  }

  if (services.hotels.length === 0 && source.name) {
    services.hotels = [build_hotel_from_root(source, dates)]
  }

  const compositions = (Array.isArray(data.passengers) && data.passengers.length > 0)
    ? build_compositions_from_passengers_fields(data.passengers, occupancy)
    : (data.compositions || build_compositions_from_occupancy(occupancy))

  const { bookToken, token: sololand_book_token } = data

  return {
    ...data,
    id: data.id || null,
    live_price: markup(base_price).live_price,
    bookToken,
    sololand_book_token,
    services,
    compositions,
  }
}
